import { ContextualInfoButtonWithText } from "@/components/buttons/ContextualInfoButton";
import AdaptationTag from "@/components/display/AdaptationTag";
import { SignificantHazards } from "@/components/status/SignificantHazards";
import { getHazardName, getRiskBandsMaxLetter } from "@/features/branding";
import type { PointDetailsPermissions } from "@/features/collection_home";
import type { HazardPlusTotal, Year } from "@/features/pe_aldd";
import { ensureGradedRiskValue, mvarToGradedRiskValue, SingleLetterRiskBand } from "@/features/risk_bands";
import { adaptationTermsConfig } from "@/types/adaptation";
import { Rcp } from "@/types/rcp";
import { getSignificantHazards } from "@/utils/hazards";
import { ArrowDownIcon } from "@phosphor-icons/react";
import { Empty, Skeleton } from "antd";
import classNames from "classnames";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MvarDerivedUnavailableNotice } from "../../collection_home/components/charts/common/MvarDerivedUnavailableNotice";
import { DeltaValue } from "../../collection_home/components/charts/common/Text";
import { isMvarDerivedDataAvailable, MetricKey } from "../../collection_home/components/charts/common/utils";
import { CompareStats, CompareStatsProps } from "../../collection_home/components/charts/CompareStats";
import { Stats } from "../../collection_home/components/charts/Stats";
import { HazardBreakdown } from "../../collection_home/components/HazardBreakdown";
import { PhysicalRisk } from "../../collection_home/components/PhysicalRisk";
import { PointTabTitle } from "../../collection_home/components/PointLevelHeaderTabs";
import { transformResultToSummary } from "../../collection_home/components/utils/pointToSummary";
import type { PathwayWithActions } from "../../collection_home/hooks/useAutoAdaptationPathways";
import type { CompareResultsMode } from "../../collection_home/types";
import type { MapDisplayPoint } from "../types";
export const PointRiskDetails = ({
  point,
  resultsMode = "baseline",
  canRunAutoAdaptation = false,
  hasAutoAdaptations = false,
  isRiskyPoint,
  year,
  rcp,
  hazard,
  isLargeSite,
  permissions,
  regionType,
  pathways,
  adaptationLoadingAssetId
}: {
  point: MapDisplayPoint;
  resultsMode?: CompareResultsMode;
  canRunAutoAdaptation?: boolean;
  hasAutoAdaptations?: boolean;
  isRiskyPoint: boolean;
  year: Year;
  rcp: Rcp;
  hazard: HazardPlusTotal;
  isLargeSite: boolean;
  permissions: PointDetailsPermissions;
  regionType?: string;
  pathways?: PathwayWithActions[];
  adaptationLoadingAssetId?: string | null;
}) => {
  const {
    t
  } = useTranslation("terms");
  const {
    t: tAssetRisk
  } = useTranslation("tools", {
    keyPrefix: "portfolios.pointLevel.assetRisk"
  });
  const adaptedAvailable = canRunAutoAdaptation && hasAutoAdaptations;
  const showAdapted = resultsMode === "enhanced_resilience" && adaptedAvailable;
  const isCompare = resultsMode === "compare" && adaptedAvailable;
  const needsAdapted = showAdapted || isCompare;

  // Compare falls back to baseline without adapted results to compare against.
  const displayMode: CompareResultsMode = resultsMode === "compare" && !adaptedAvailable ? "baseline" : resultsMode;
  const adaptedAction = needsAdapted ? pathways?.find(p => p.adaptation_type === "enhanced_resilience")?.actions?.[0] : undefined;
  const transformedAsset = useMemo(() => {
    if (!point?.asset) return null;
    return transformResultToSummary(point, point.asset, regionType);
  }, [point, regionType]);
  const adaptedTransformedAsset = useMemo(() => {
    if (!adaptedAction?.result) return null;
    return transformResultToSummary(point, adaptedAction, regionType);
  }, [point, adaptedAction, regionType]);
  const activeAsset = showAdapted ? adaptedTransformedAsset : transformedAsset;
  const result = transformedAsset?.find(r => r.climate_scenario === rcp && r.hazard === hazard) ?? null;
  const activeResult = activeAsset?.find(r => r.climate_scenario === rcp && r.hazard === hazard) ?? null;
  const adaptedResult = adaptedTransformedAsset?.find(r => r.climate_scenario === rcp && r.hazard === hazard) ?? null;
  const isAdaptedLoading = needsAdapted && adaptationLoadingAssetId === point.asset?.uuid;
  const adaptedMissing = needsAdapted && !isAdaptedLoading && !adaptedAction?.result;
  if (!transformedAsset || !result || !year) return null;
  if (isAdaptedLoading) return <Skeleton active />;
  if (adaptedMissing) return <Empty description={`No ${t("adaptationTypes.enhanced_resilience")} results for selection`} image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  const tag = canRunAutoAdaptation && <AdaptationTag type={displayMode} />;
  const footer = <div className="flex flex-col gap-1">
      {/* {tag} */}
      {`${t(`scenarios.${rcp}.short`)}, ${getHazardName(hazard)}, ${year}`}
    </div>;
  const adaptedRating = (() => {
    const mvar = adaptedResult?.avg_damage_mtd?.[year];
    return mvar != null ? mvarToGradedRiskValue(mvar, getRiskBandsMaxLetter(regionType)) : "?";
  })();
  const baselineRating = (() => {
    if ("ratings" in point.response) {
      const ratings = point.response.ratings || [];
      const yearRating = ratings.find(r => r.year === year);
      const ratingValue = yearRating?.rating ?? ratings[0]?.rating;
      return ratingValue ? ensureGradedRiskValue(ratingValue) : "?";
    }
    if ("end_of_mortgage_term" in point.response) {
      const ratingValue = point.response.end_of_mortgage_term?.rating;
      return ratingValue ? ensureGradedRiskValue(ratingValue) : "?";
    }
    return "?";
  })();
  const rating = showAdapted ? adaptedRating : baselineRating;

  // Significant hazards are derived (top-2 hazards over the damage threshold), so the adapted
  // result can produce its own set — adaptation can push a hazard below the threshold.
  const adaptedSignificantHazards = (() => {
    const scenarioData = adaptedAction?.result.results[rcp];
    const incorporatedHz = adaptedAction?.result.metadata.incorporated_hazards;
    if (!scenarioData || !incorporatedHz) return null;
    return {
      significantHz: getSignificantHazards(scenarioData.damage.by_hazard, year),
      incorporatedHz
    };
  })();
  const baselineSignificantHazards = (() => {
    if ("top_significant_hazards" in point.response && point.metadata) {
      return {
        significantHz: point.response.top_significant_hazards,
        incorporatedHz: point.metadata.incorporated_hazards
      };
    }
    return null;
  })();
  const significantHazards = showAdapted ? adaptedSignificantHazards : baselineSignificantHazards;
  const visibleStatsCount = [permissions.showMvar, permissions.showTtip, permissions.showProductivityLoss, permissions.showFailureProbability].filter(Boolean).length;

  // Under compare every tile is multi-row, so the rating tile does not span for balance.
  const simpleRiskRowSpanClassName = isCompare || visibleStatsCount <= 2 ? "row-span-1" : "row-span-2";
  const placeholder = "";
  const mvarAvailable = isMvarDerivedDataAvailable(hazard);
  const toPercent = (value?: number) => Number.isFinite(value) ? (value as number) * 100 : null;

  /** Baseline / adapted / absolute change / relative change, matching the aggregated view. */
  const renderCompareTile = ({
    label,
    metric,
    unit,
    unitPosition,
    baseline,
    adapted
  }: {
    label: string;
    metric: MetricKey;
    unit: string;
    unitPosition?: CompareStatsProps["unitPosition"];
    baseline: number | null;
    adapted: number | null;
  }) => <CompareStats contextId="metrics" label={label} unit={unit} unitPosition={unitPosition} footer={footer} tag={tag} values={[{
    label: t("adaptationTypes.baseline"),
    value: baseline ?? undefined,
    color: adaptationTermsConfig.baseline.color
  }, {
    label: t("adaptationTypes.enhanced_resilience"),
    value: adapted ?? undefined,
    color: adaptationTermsConfig.enhanced_resilience.color
  }, {
    label: t("absoluteChange"),
    color: adaptationTermsConfig.absoluteChange.color,
    render: () => <DeltaValue mode="absoluteChange" metric={metric} baseline={baseline} compare={adapted} />
  }, {
    label: t("relativeChange"),
    color: adaptationTermsConfig.relativeChange.color,
    render: () => <DeltaValue mode="relativeChange" metric={metric} baseline={baseline} compare={adapted} />
  }]} data-sentry-element="CompareStats" data-sentry-component="renderCompareTile" data-sentry-source-file="PointRiskDetails.tsx" />;
  return <>
      {activeAsset && activeResult && <>
          <div className="grid grid-cols-2 gap-2 @2xl:grid-cols-3">
            <div className="col-span-2 flex items-start justify-start gap-2 @2xl:col-span-3">
              <h2 className="!text-base font-semibold tracking-[-0.01em] text-gray-900 @2xl/pointLevelWrapper:!text-lg">
                {PointTabTitle["risk"]} Summary
              </h2>
              {tag}
            </div>
            {!mvarAvailable && <MvarDerivedUnavailableNotice message={tAssetRisk("charts.mvar.unavailableMessage")} className="col-span-2 rounded-lg @2xl:col-span-3" />}
            <div className={classNames("row-span-2 flex flex-col justify-between gap-1 rounded-2xl border border-gray-200 p-3 shadow-sm @3xl:p-4", simpleRiskRowSpanClassName)}>
              <div className="flex items-start justify-between gap-2">
                <ContextualInfoButtonWithText id="simpleRiskRatings">
                  <div className="text-sm font-medium tracking-[-0.01em] text-gray-900">{t("simpleRiskRatings")}</div>
                </ContextualInfoButtonWithText>
                {tag}
              </div>
              {year && (!mvarAvailable ? <div className="mx-auto w-full max-w-[148px]">
                    <SingleLetterRiskBand rating="?" notApplicable fullWidth />
                  </div> : isCompare ? <div className="mx-auto flex w-full flex-col items-center justify-center gap-2">
                    <div className="flex flex-col items-center gap-1">
                      <SingleLetterRiskBand rating={baselineRating} size={72} />
                      <span className="text-xs font-medium" style={{
                color: adaptationTermsConfig.baseline.color
              }}>
                        {t("adaptationTypes.baseline")}
                      </span>
                    </div>
                    <ArrowDownIcon size={16} aria-hidden className="text-gray-400" />
                    <div className="flex flex-col items-center gap-1">
                      <SingleLetterRiskBand rating={adaptedRating} size={72} />
                      <span className="text-xs font-medium" style={{
                color: adaptationTermsConfig.enhanced_resilience.color
              }}>
                        {t("adaptationTypes.enhanced_resilience")}
                      </span>
                    </div>
                  </div> : <div className="mx-auto w-full max-w-[148px]">
                    <SingleLetterRiskBand rating={rating} fullWidth />
                  </div>)}
              {footer && <div className="text-xs font-medium text-gray-400">{footer}</div>}
            </div>
            {permissions.showMvar && (!mvarAvailable ? <Stats contextId="metrics" label={tAssetRisk("charts.mvar.title")} value="" footer={footer} tag={tag} notApplicable /> : isCompare ? renderCompareTile({
          label: tAssetRisk("charts.mvar.title"),
          metric: "avg_damage_mtd",
          unit: "%",
          baseline: toPercent(result.avg_damage_mtd?.[year]),
          adapted: toPercent(adaptedResult?.avg_damage_mtd?.[year])
        }) : <Stats contextId="metrics" label={tAssetRisk("charts.mvar.title")} value={activeResult.avg_damage_mtd[year] * 100} footer={footer} tag={tag} unit="%" />)}
            {permissions.showTtip && (!mvarAvailable ? <Stats contextId="metrics" label={tAssetRisk("charts.ttip.title")} value="" footer={footer} tag={tag} notApplicable /> : isCompare ? renderCompareTile({
          label: tAssetRisk("charts.ttip.title"),
          metric: "ttip",
          unit: "$",
          unitPosition: "start",
          baseline: result.ttip?.[year] ?? null,
          adapted: adaptedResult?.ttip?.[year] ?? null
        }) : <Stats contextId="metrics" label={tAssetRisk("charts.ttip.title")} value={activeResult.ttip?.[year] ?? 0} footer={footer} tag={tag} unit="$" unitPosition="start" />)}
            {permissions.showProductivityLoss && (isCompare ? renderCompareTile({
          label: tAssetRisk("charts.productivityLoss.title"),
          metric: "productivity_loss",
          unit: "%",
          baseline: toPercent(result.productivity_loss?.[year]),
          adapted: toPercent(adaptedResult?.productivity_loss?.[year])
        }) : <Stats contextId="metrics" label={tAssetRisk("charts.productivityLoss.title")} value={Number.isFinite(activeResult.productivity_loss?.[year]) ? activeResult.productivity_loss[year] * 100 : placeholder} footer={footer} tag={tag} unit="%" />)}
            {permissions.showFailureProbability && (isCompare ? renderCompareTile({
          label: tAssetRisk("charts.failureProbability.title"),
          metric: "avg_failure_mtd",
          unit: "%",
          baseline: toPercent(result.avg_failure_mtd?.[year]),
          adapted: toPercent(adaptedResult?.avg_failure_mtd?.[year])
        }) : <Stats contextId="metrics" label={tAssetRisk("charts.failureProbability.title")} value={Number.isFinite(activeResult.avg_failure_mtd?.[year]) ? activeResult.avg_failure_mtd[year] * 100 : placeholder} footer={footer} tag={tag} unit="%" />)}

            {(isCompare ? baselineSignificantHazards : significantHazards) && <div className={classNames({
          "rounded-2xl border border-gray-200 p-3 shadow-sm @3xl:p-4": true,
          "col-span-2 @2xl:col-span-3": !isLargeSite || isLargeSite && isRiskyPoint,
          "col-span-1 row-span-2 @2xl:col-span-2": isLargeSite && !isRiskyPoint
        })}>
                {isCompare ? <>
                    <div className="flex items-start justify-between gap-2">
                      <ContextualInfoButtonWithText id="significantHazards" rootClassName="text-left !font-semibold text-base">
                        <span>Significant Hazards at {year}</span>
                      </ContextualInfoButtonWithText>
                      {tag}
                    </div>
                    <div className="mt-2 flex flex-col gap-3 divide-y divide-gray-100 pt-3">
                      {baselineSignificantHazards && <SignificantHazards noTitle significantHz={baselineSignificantHazards.significantHz} incorporatedHz={baselineSignificantHazards.incorporatedHz} year={year} tag={<AdaptationTag type="baseline" />} />}
                      {adaptedSignificantHazards && <SignificantHazards noTitle significantHz={adaptedSignificantHazards.significantHz} incorporatedHz={adaptedSignificantHazards.incorporatedHz} year={year} tag={<AdaptationTag type="enhanced_resilience" />} />}
                    </div>
                  </> : significantHazards && <SignificantHazards significantHz={significantHazards.significantHz} incorporatedHz={significantHazards.incorporatedHz} year={year} tag={tag} />}
              </div>}
          </div>

          <PhysicalRisk id="physical-risk-sidebar" data={activeAsset} adaptedData={isCompare ? adaptedTransformedAsset ?? undefined : undefined} translationPrefix="portfolios.pointLevel.assetRisk" permissions={permissions} isLargeSite={isLargeSite} compareResultsMode={canRunAutoAdaptation ? displayMode : undefined} tag={tag} />

          <HazardBreakdown id="hazard-breakdown-sidebar" data={activeAsset} compareData={isCompare ? adaptedTransformedAsset ?? undefined : undefined} showBreakdownTable={isCompare} translationPrefixCharts="portfolios.pointLevel.assetRisk" translationPrefix="portfolios.pointLevel.hazardBreakdown" permissions={permissions} isLargeSite={isLargeSite} compareResultsMode={canRunAutoAdaptation ? displayMode : undefined} comparisonType={displayMode === "compare" ? "enhanced_resilience" : displayMode} tag={tag} />
        </>}
    </>;
};